import {
  TreeViewResponseItem,
  TreeViewTableItem,
} from "@/apis/preview/v2/types";
import {
  getHighlightId,
  useCellHighlight,
  useHighlightSelectionStore,
} from "@/hooks/use-cell-highlight";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/styles/ui/context-menu";
import { AccountingPeriod } from "@/types/enums";
import {
  Container,
  ContainerPositionChangeEvent,
  DndTreeSortable,
  FlattenedItem,
  PositionChangeEvent,
} from "@snurf198/dnd-tree-sortable";
import { Plus, Trash2 } from "lucide-react";
import { pushModal } from "@/stores/modal";
import { useCallback, useMemo } from "react";
import { useWorkbookIdAndTableId } from "../../hook";
import { useReorderTag } from "../../table/use-editable-preview";
import { getBegend } from "@/utils/begend";
import { DepthIcon } from "../tree/depth-icon";
import TagGroupContainer from "./tag-group-container";
import TreeTagItem from "./tree-tag-item";
import { RemoveTagFromCellsConfirmModal } from "./remove-tag-from-cells-confirm-modal";
import { CustomContainer, CustomFlattenedItem } from "./type";
import { useCdlOverrideUpdateActions } from "./use-cdl-override-update-actions";
import type { RemoveTagFromCellsArgs } from "./use-remove-tag-from-cells";

const CELL_TAGGABLE_TYPES = new Set(["LINE_ITEM", "MEMBER"]);

interface TreeItemsProps {
  treeViewItems: TreeViewResponseItem[];
  roleKey: string;
  dimension: "LINE_ITEM" | "AXIS" | "TEXTBLOCK";
  item: TreeViewTableItem;
  /**
   * 주어지면 매핑탭 전용 동작이 켜진다 — 행 hover 로 셀 하이라이트, 휴지통으로 셀 태그 일괄 제거.
   * 헤더의 태그 빌딩 페이지는 이 prop 을 넘기지 않는다.
   */
  onRemoveTag?: (args: RemoveTagFromCellsArgs) => void;
}

/**
 * Converts flat array of tree items to hierarchical structure
 * Groups items by LINE_ITEMS or AXIS containers
 */
const buildTreeStructure = ({
  dimension,
  items,
  tableLabel,
  roleKey,
}: {
  dimension: "LINE_ITEM" | "AXIS" | "TEXTBLOCK";
  items: TreeViewResponseItem[];
  tableLabel: string;
  roleKey: string;
}): Container[] => {
  /**
   * TreeViewResponseItem(flatten된 tree)를 여러 묶음(container)의 tree 구조로 변환하는 함수. depth가 0인 아이템을 root로 하는 tree로 묶는다
   */
  if (items.length === 0) return [];

  const containers: CustomContainer[] = [];
  let currentContainer: CustomContainer | null = null;
  const stack: { item: TreeViewResponseItem; treeItem: any }[] = [];
  const rootDepth = dimension === "LINE_ITEM" ? 0 : 2;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    // Create tree item with all properties from TreeViewResponseItem
    const treeItem = {
      id: `${item.id}/${getBegend(item.tagKoLabel)}`,
      tagKey: item.tagKey,
      tagKoLabel: item.tagKoLabel,
      tagEnLabel: item.tagEnLabel,
      tagType: item.tagType,
      depth: item.depth,
      isExtendedTag: item.isExtendedTag,
      isSum: item.isSum,
      isStructuralOverride: item.isStructuralOverride ?? false,
      autoAdded: item.autoAdded ?? false,
      children: [],
    };

    // If depth is 0, start a new container
    if (item.depth < rootDepth) {
      continue;
    }

    if (item.depth === rootDepth) {
      currentContainer = {
        id: `${item.tagKoLabel}-container-${containers.length}`,
        name: item.tagKoLabel,
        children: [],
        headerShownItems: [item],
        depth: 0,
        roleKey,
        tagKey: item.tagKey,
        tagKoLabel: item.tagKoLabel,
        tagEnLabel: item.tagEnLabel,
        tagType: item.tagType,
        isExtendedTag: item.isExtendedTag,
        isSum: item.isSum,
      };
      containers.push(currentContainer);
      stack.length = 0; // Clear stack for new container
      stack.push({ item, treeItem: currentContainer });
    } else if (currentContainer) {
      while (
        stack.length > 0 &&
        stack[stack.length - 1].item.depth >= item.depth
      ) {
        stack.pop();
      }

      const isHeaderShownTags =
        treeItem.depth < (dimension === "LINE_ITEM" ? 2 : 4);

      if (isHeaderShownTags) {
        currentContainer.headerShownItems.push(item);
      } else if (stack.length > 0) {
        const parent = stack[stack.length - 1].treeItem;
        parent.children.push(treeItem);
        stack.push({ item, treeItem });
      }
    }
  }

  return containers;
};

export const TreeItems = ({
  treeViewItems,
  roleKey,
  dimension,
  item,
  onRemoveTag,
}: TreeItemsProps) => {
  const sortableItems: Container[] = useMemo(() => {
    return buildTreeStructure({
      dimension,
      items: treeViewItems,
      tableLabel: item.label,
      roleKey,
    });
  }, [dimension, treeViewItems, item.label, roleKey]);
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const {
    highlightByTagKey,
    previewByTagKey,
    endPreview,
    holdPreview,
    releasePreview,
    getTaggedCellCount: getRoleTaggedCellCount,
    clearHighlight,
  } = useCellHighlight(workbookId, tableId);
  const selectedId = useHighlightSelectionStore((state) => state.selectedId);

  // 같은 tagKey 가 기초/기말 로 트리에 중복 노출되는 태그(=period 로 구분되는 태그)의
  // 집합. 이 집합에 속한 태그만 하이라이트 시 셀 period 까지 비교한다.
  const periodScopedTagKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const treeItem of treeViewItems) {
      if (getBegend(treeItem.tagKoLabel) !== AccountingPeriod.NONE) {
        keys.add(treeItem.tagKey);
      }
    }
    return keys;
  }, [treeViewItems]);

  const handleHighlight = useCallback(
    (tagKey: string, tagKoLabel: string) => {
      highlightByTagKey(
        roleKey,
        tagKey,
        getBegend(tagKoLabel),
        periodScopedTagKeys.has(tagKey),
      );
    },
    [highlightByTagKey, periodScopedTagKeys, roleKey],
  );
  const handlePreview = useCallback(
    (tagKey: string, tagKoLabel: string) => {
      previewByTagKey(
        roleKey,
        tagKey,
        getBegend(tagKoLabel),
        periodScopedTagKeys.has(tagKey),
      );
    },
    [previewByTagKey, periodScopedTagKeys, roleKey],
  );

  const getTaggedCellCount = useCallback(
    (tagKey: string) => getRoleTaggedCellCount(roleKey, tagKey),
    [getRoleTaggedCellCount, roleKey],
  );

  const openRemoveTagModal = useCallback(
    (
      target: Pick<TreeViewResponseItem, "tagKey" | "tagKoLabel">,
      cellCount: number,
    ) => {
      if (!onRemoveTag) return;
      pushModal(RemoveTagFromCellsConfirmModal, {
        tagKoLabel: target.tagKoLabel,
        cellCount,
        includesAllPeriods: periodScopedTagKeys.has(target.tagKey),
        onConfirm: () => {
          clearHighlight();
          onRemoveTag({
            tagKey: target.tagKey,
            roleKey,
            tagKoLabel: target.tagKoLabel,
          });
        },
        onOpen: () => holdPreview(roleKey, target.tagKey),
        onDismiss: releasePreview,
      });
    },
    [
      onRemoveTag,
      holdPreview,
      releasePreview,
      clearHighlight,
      roleKey,
      periodScopedTagKeys,
    ],
  );

  const { mutate: reorderTagMutate } = useReorderTag();

  const { handleAddChildTag, handleAddChildTagToContainer, handleRemoveTag } =
    useCdlOverrideUpdateActions(dimension, roleKey, treeViewItems);

  /**
   * 트리 아이템에서 tagKey로 아이템을 찾는 헬퍼 함수
   */
  const findItemById = useCallback(
    (id: string): TreeViewResponseItem | undefined => {
      return treeViewItems.find(
        (treeItem) => `${treeItem.id}/${getBegend(treeItem.tagKoLabel)}` === id,
      );
    },
    [treeViewItems],
  );

  /**
   * 컨테이너에서 ID로 컨테이너를 찾는 헬퍼 함수
   */
  const findContainerById = useCallback(
    (containerId: string): CustomContainer | undefined => {
      return sortableItems.find((container) => container.id === containerId) as
        | CustomContainer
        | undefined;
    },
    [sortableItems],
  );

  /**
   * 아이템이 속한 원래 컨테이너를 찾는 헬퍼 함수
   */
  const findOriginalContainerByItemId = useCallback(
    (itemId: string): CustomContainer | undefined => {
      return sortableItems.find((container) => {
        const customContainer = container as CustomContainer;
        // children에서 재귀적으로 아이템 찾기
        const findInChildren = (children: any[]): boolean => {
          for (const child of children) {
            if (child.id === itemId) return true;
            if (child.children && findInChildren(child.children)) return true;
          }
          return false;
        };
        return findInChildren(customContainer.children);
      }) as CustomContainer | undefined;
    },
    [sortableItems],
  );

  /**
   * 아이템 위치 변경 시 백엔드 API 호출
   */
  const handlePositionChange = useCallback(
    (event: PositionChangeEvent) => {
      const { targetId, parentId, nextId, containerId } = event;

      // 이동된 아이템 찾기
      const targetItem = findItemById(targetId);
      if (!targetItem) {
        console.warn("Target item not found:", targetId);
        return;
      }

      // 원래 컨테이너와 새 컨테이너 비교
      const originalContainer = findOriginalContainerByItemId(targetId);
      const newContainer = findContainerById(containerId);
      const isContainerChanged =
        originalContainer &&
        newContainer &&
        originalContainer.id !== newContainer.id;

      // 부모 아이템 찾기
      let parentTagKey: string;
      let parentBegend: AccountingPeriod;

      if (isContainerChanged && parentId === null) {
        // 컨테이너가 변경된 경우: TABLE을 부모로 설정
        const memberItem = newContainer.headerShownItems.find(
          (h) => h.tagType === "MEMBER",
        );
        parentTagKey = memberItem?.tagKey || "";
        parentBegend = getBegend(memberItem?.tagKoLabel);
      } else if (parentId) {
        // 같은 컨테이너 내에서 이동, parentId가 있는 경우
        const parentItem = findItemById(parentId);
        if (parentItem) {
          parentTagKey = parentItem.tagKey;
          parentBegend = getBegend(parentItem.tagKoLabel);
        } else {
          console.warn("Parent item not found:", parentId);
          return;
        }
      } else {
        // 같은 컨테이너 내에서 루트 레벨로 이동 - 컨테이너의 헤더 아이템을 부모로 사용
        const container = findContainerById(containerId);
        if (container && container.headerShownItems.length > 0) {
          // LINE_ITEM: depth 1인 LINE_ITEMS 타입 아이템을 부모로
          // AXIS: depth 3인 AXIS 타입 아이템을 부모로
          const headerItems = container.headerShownItems;
          const rootParent =
            dimension === "LINE_ITEM"
              ? headerItems.find((h) => h.tagType === "LINE_ITEMS") ||
                headerItems[headerItems.length - 1]
              : headerItems.find((h) => h.tagType === "MEMBER") ||
                headerItems[headerItems.length - 1];

          parentTagKey = rootParent?.tagKey || container.tagKey || "";
          parentBegend = getBegend(rootParent?.tagKoLabel);
        } else {
          console.warn("Container not found:", containerId);
          return;
        }
      }

      // 다음 형제 아이템 찾기
      let nextTagKey: string | null = null;
      let nextBegend: AccountingPeriod = AccountingPeriod.NONE;

      if (nextId) {
        const nextItem = findItemById(nextId);
        if (nextItem) {
          nextTagKey = nextItem.tagKey;
          nextBegend = getBegend(nextItem.tagKoLabel);
        }
      }

      if (!tableId) {
        console.warn("Table ID not found");
        return;
      }

      // merged 트리에서 target의 절대 인덱스(newPosition) 계산
      const calculatePosition = (): number => {
        const container = event.items.find((c) => c.id === containerId);
        if (!container) return 0;

        const findIndexInParent = (
          children: { id: string; children: any[] }[],
          pid: string | null,
        ): number => {
          if (pid === null) {
            return children.findIndex((c) => c.id === targetId);
          }
          for (const child of children) {
            if (child.id === pid) {
              return child.children.findIndex(
                (c: { id: string }) => c.id === targetId,
              );
            }
            const result = findIndexInParent(child.children, pid);
            if (result >= 0) return result;
          }
          return -1;
        };

        const idx = findIndexInParent(container.children, parentId);
        return idx >= 0 ? idx : 0;
      };

      reorderTagMutate(
        {
          roleKey,
          tagKey: targetItem.tagKey,
          begend: getBegend(targetItem.tagKoLabel),
          newParentTagKey: parentTagKey || null,
          newParentBegend: parentBegend,
          newPosition: calculatePosition(),
        },
        {
          onError: () => {
            event.setItems(
              buildTreeStructure({
                dimension,
                items: treeViewItems,
                tableLabel: item.label,
                roleKey,
              }),
            );
          },
        },
      );
      return;
    },
    [
      findItemById,
      findContainerById,
      findOriginalContainerByItemId,
      treeViewItems,
      dimension,
      roleKey,
      tableId,
      reorderTagMutate,
      item.label,
    ],
  );

  /**
   * 컨테이너(AXIS 그룹) 위치 변경 시 백엔드 API 호출
   */
  const handleContainerPositionChange = useCallback(
    (event: ContainerPositionChangeEvent) => {
      if (dimension !== "AXIS") return;

      const { containerId, nextContainerId } = event;

      // 이동된 컨테이너 찾기
      const movedContainer = findContainerById(containerId);
      if (!movedContainer) {
        console.warn("Moved container not found:", containerId);
        return;
      }

      // 컨테이너의 AXIS 아이템 찾기
      const axisItem = movedContainer.headerShownItems.find(
        (h) => h.tagType === "AXIS",
      );
      if (!axisItem) {
        console.warn("AXIS item not found in container:", containerId);
        return;
      }

      // 다음 컨테이너의 AXIS 아이템 찾기
      let nextTagKey: string | null = null;
      let nextBegend: AccountingPeriod = AccountingPeriod.NONE;

      if (nextContainerId) {
        const nextContainer = findContainerById(nextContainerId);
        if (nextContainer) {
          const nextAxisItem = nextContainer.headerShownItems.find(
            (h) => h.tagType === "AXIS",
          );
          if (nextAxisItem) {
            nextTagKey = nextAxisItem.tagKey;
            nextBegend = getBegend(nextAxisItem.tagKoLabel);
          }
        }
      }

      // TABLE을 부모로 설정 (AXIS는 테이블 직속)
      const tableItem = treeViewItems.find((i) => i.tagType === "TABLE");
      const parentTagKey = tableItem?.tagKey || "";
      const parentBegend = getBegend(tableItem?.tagKoLabel);

      if (!tableId) {
        console.warn("Table ID not found");
        return;
      }

      const newPosition = event.items.findIndex((c) => c.id === containerId);
      reorderTagMutate({
        roleKey,
        tagKey: axisItem.tagKey,
        begend: getBegend(axisItem.tagKoLabel),
        newParentTagKey: parentTagKey || null,
        newParentBegend: parentBegend,
        newPosition: newPosition >= 0 ? newPosition : 0,
      });
      return;
    },
    [
      dimension,
      findContainerById,
      treeViewItems,
      roleKey,
      tableId,
      reorderTagMutate,
    ],
  );

  return (
    <DndTreeSortable
      items={sortableItems}
      renderItem={({ item }: { item: FlattenedItem }) => {
        const customItem = item as CustomFlattenedItem;
        // 셀에 실제로 붙는 태그만 hover 미리보기·휴지통 대상이다.
        const cellTaggable =
          !!onRemoveTag && CELL_TAGGABLE_TYPES.has(customItem.tagType);
        const taggedCellCount = cellTaggable
          ? getTaggedCellCount(customItem.tagKey)
          : 0;
        const treeTagItem = (
          <TreeTagItem
            className="w-full"
            item={customItem}
            isSelected={
              selectedId ===
              getHighlightId(roleKey, customItem.tagKey, customItem.tagKoLabel)
            }
            onClick={() =>
              handleHighlight(customItem.tagKey, customItem.tagKoLabel)
            }
            onHoverStart={
              cellTaggable
                ? () => handlePreview(customItem.tagKey, customItem.tagKoLabel)
                : undefined
            }
            onHoverEnd={cellTaggable ? endPreview : undefined}
            onRemove={
              taggedCellCount > 0
                ? () => openRemoveTagModal(customItem, taggedCellCount)
                : undefined
            }
            tagDetailRoleKey={onRemoveTag ? roleKey : undefined}
          />
        );

        if (dimension === "TEXTBLOCK") {
          return treeTagItem;
        }

        return (
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div>{treeTagItem}</div>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem
                onClick={() => handleAddChildTag(customItem)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                하위 태그 추가
              </ContextMenuItem>
              {customItem.isStructuralOverride && (
                <ContextMenuItem
                  onClick={() => handleRemoveTag(customItem)}
                  className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                  태그 삭제
                </ContextMenuItem>
              )}
            </ContextMenuContent>
          </ContextMenu>
        );
      }}
      mouseSensorActivationDistance={30}
      renderContainer={({ container, children, handleProps, isDragging }) => {
        const tagGroupContainer = (
          <TagGroupContainer
            key={container.id}
            dimension={dimension}
            container={container as CustomContainer}
            children={children as React.ReactNode}
            handleProps={dimension === "AXIS" ? handleProps : undefined}
            isDragging={isDragging}
            selectedId={selectedId}
            onItemClick={handleHighlight}
            onItemHoverStart={onRemoveTag ? handlePreview : undefined}
            onItemHoverEnd={onRemoveTag ? endPreview : undefined}
            getTaggedCellCount={onRemoveTag ? getTaggedCellCount : undefined}
            onItemRemove={onRemoveTag ? openRemoveTagModal : undefined}
            tagDetailRoleKey={onRemoveTag ? roleKey : undefined}
          />
        );

        if (dimension === "TEXTBLOCK") {
          return tagGroupContainer;
        }

        return (
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div>{tagGroupContainer}</div>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem
                onClick={() =>
                  handleAddChildTagToContainer(container as CustomContainer)
                }
                className="flex items-center gap-2 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                하위 태그 추가
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        );
      }}
      itemGap={16}
      onPositionChange={handlePositionChange}
      onContainerPositionChange={handleContainerPositionChange}
      linkIcon={
        <div className="flex flex-row pl-[12px] translate-y-[-16px]">
          <DepthIcon />
        </div>
      }
    />
  );
};
